<template>
  <a-modal
    :visible="visible"
    :title="`Gestion des ${typeLabel}(s) - ${
      bordereauData.numero || 'Bordereau'
    }`"
    width="80%"
    :footer="null"
    @cancel="handleCancel"
    :confirmLoading="loading"
  >
    <div v-if="bordereauData && bordereauData.chequesDetails">
      <!-- Bordereau Info -->
      <a-alert
        :message="`Bordereau: ${bordereauData.numero}`"
        :description="`${
          bordereauData.chequesDetails.length
        } ${typeLabelPlural} - Total: ${formatAmount(
          bordereauData.montantTotal || 0
        )}`"
        type="info"
        show-icon
        style="margin-bottom: 16px"
      />

      <!-- Cheques Table -->
      <a-table
        :dataSource="bordereauData.chequesDetails"
        :columns="dynamicChequeColumns"
        :pagination="{ pageSize: 10, showSizeChanger: true }"
        size="middle"
        :loading="loading"
        rowKey="_id"
        style="border: 1px solid #d9d9d9; border-radius: 6px"
      >
        <template slot="bankStatus" slot-scope="text, record">
          <a-select
            v-model="record.bankStatus"
            size="small"
            style="width: 140px"
            @change="(value) => updateChequeBankStatus(record._id, value)"
            :loading="record.updating"
            :disabled="
              record.bankStatus === 'VALIDATED' ||
              record.status === 'REPLACED' ||
              record.status === 'CANCELLED'
            "
          >
            <a-select-option value="PENDING">
              <a-icon type="clock-circle" style="color: #faad14" />
              {{ $t("status.PENDING") }}
            </a-select-option>
            <a-select-option value="VALIDATED">
              <a-icon type="check-circle" style="color: #52c41a" />
              {{ $t("status.VALIDATED") }}
            </a-select-option>
            <a-select-option value="REJECTED">
              <a-icon type="close-circle" style="color: #ff4d4f" />
              {{ $t("status.REJECTED") }}
            </a-select-option>
          </a-select>
        </template>
        <template slot="status" slot-scope="text, record">
          <a-tag :color="getChequeStatutColor(text)">{{
            $t("status." + text)
          }}</a-tag>
        </template>
        <template slot="montant" slot-scope="text">
          <span class="amount-cell">
            {{ formatAmount(text) }}
          </span>
        </template>
      </a-table>

      <!-- Summary -->
      <div
        style="
          margin-top: 16px;
          padding: 12px;
          background-color: #f5f5f5;
          border-radius: 6px;
        "
      >
        <a-row :gutter="16">
          <a-col :span="6">
            <a-statistic
              :title="`Total ${typeLabelPlural}`"
              :value="bordereauData.chequesDetails.length"
              :value-style="{ color: '#1890ff' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="Validés"
              :value="validatedCount"
              :value-style="{ color: '#52c41a' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="En Attente"
              :value="pendingCount"
              :value-style="{ color: '#faad14' }"
            />
          </a-col>
          <a-col :span="6">
            <a-statistic
              title="Rejetés"
              :value="rejectedCount"
              :value-style="{ color: '#ff4d4f' }"
            />
          </a-col>
        </a-row>
      </div>
    </div>

    <div v-else style="text-align: center; padding: 40px">
      <a-empty
        :description="`Aucun ${typeLabelSingular} trouvé dans ce bordereau`"
      />
    </div>

    <!-- Action Buttons -->
    <div style="text-align: right; margin-top: 24px">
      <a-button @click="handleCancel" :disabled="loading"> Fermer </a-button>
    </div>
  </a-modal>
</template>

<script>
import apiClient from "@/services/axios";
import { getChequeStatutColor } from "@/utilities/settings";
import moment from "moment";
import { formatCurrency as formatCurrencyUtil, getBuildingCurrency } from "@/utilities/countrySpecific";

export default {
  name: "ChequesManagementModal",
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    bordereauData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      currency: null,
    };
  },
  computed: {
    bordereauType() {
      return this.bordereauData?.type || "CHEQUE";
    },
    typeLabel() {
      return this.bordereauType === "CHEQUE"
        ? this.$t("recette.paymentMethod.CHEQUE")
        : this.$t("recette.paymentMethod.TRAITE");
    },
    typeLabelPlural() {
      return this.bordereauType === "CHEQUE"
        ? this.$t("recette.associatedCheques").toLowerCase()
        : this.$t("recette.associatedTraites").toLowerCase();
    },
    typeLabelSingular() {
      return this.bordereauType === "CHEQUE"
        ? this.$t("recette.paymentMethod.CHEQUE").toLowerCase()
        : this.$t("recette.paymentMethod.TRAITE").toLowerCase();
    },
    // Dynamic columns based on bordereau type
    dynamicChequeColumns() {
      const baseColumns = [
        {
          title: this.$t("recette.numero"),
          dataIndex: "num",
          key: "num",
          width: 100,
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {text}
              </span>
            );
          },
        },
        {
          title: this.$t("recette.stats.generalStatus"),
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
          width: 120,
        },
        {
          title: this.$t("recette.banque"),
          dataIndex: "bank",
          key: "bank",
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {text ? (text.includes("-") ? text.split("-")[0] : text) : "--"}
              </span>
            );
          },
          width: 120,
        },
      ];

      // Add appropriate column based on bordereau type
      if (this.bordereauType === "CHEQUE") {
        baseColumns.push({
          title: this.$t("recette.titulaire"),
          dataIndex: "titulaire",
          key: "titulaire",
          ellipsis: true,
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {text || "--"}
              </span>
            );
          },
          width: 150,
        });

        // Add dateEcheance column for CHEQUE type
        baseColumns.push({
          title: this.$t("recette.dateEcheance"),
          dataIndex: "dateEchance",
          key: "dateEchance",
          width: 100,
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            const dateEchance = record.dateEchance;
            const displayDate =
              dateEchance && moment(dateEchance).isValid()
                ? moment(dateEchance).format("DD/MM/YYYY")
                : "--";

            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {displayDate}
              </span>
            );
          },
          width: 150,
        });
      } else {
        // For TRAITE, show RIB column
        baseColumns.push({
          title: "RIB",
          dataIndex: "rib",
          key: "rib",
          ellipsis: true,
          customRender: (text, record) => {
            const isDisabled = record.bankStatus === "VALIDATED";
            return (
              <span
                style={{
                  color: isDisabled ? "#999" : "inherit",
                }}
              >
                {text || "--"}
              </span>
            );
          },
          width: 150,
        });
      }

      // Add remaining columns
      baseColumns.push(
        {
          title: this.$t("recette.montant"),
          dataIndex: "montant",
          key: "montant",
          scopedSlots: { customRender: "montant" },
          width: 120,
        },
        {
          title: this.$t("all.edit"),
          dataIndex: "bankStatus",
          key: "bankStatus",
          scopedSlots: { customRender: "bankStatus" },
          width: 150,
        }
      );

      return baseColumns;
    },
    validatedCount() {
      return (
        this.bordereauData.chequesDetails?.filter(
          (c) => c.bankStatus === "VALIDATED"
        ).length || 0
      );
    },
    pendingCount() {
      return (
        this.bordereauData.chequesDetails?.filter(
          (c) => c.bankStatus === "PENDING"
        ).length || 0
      );
    },
    rejectedCount() {
      return (
        this.bordereauData.chequesDetails?.filter(
          (c) => c.bankStatus === "REJECTED"
        ).length || 0
      );
    },
  },
  created() {
    this.currency = getBuildingCurrency();
  },
  watch: {
    visible(newVal) {
      if (newVal && this.bordereauData && this.bordereauData._id) {
        this.fetchFullBordereauData();
      }
    },
  },
  methods: {
    getChequeStatutColor,
    formatAmount(amount) {
      return formatCurrencyUtil(amount || 0, this.currency);
    },

    async fetchFullBordereauData() {
      try {
        this.loading = true;
        const response = await apiClient.get(
          `/compV3/bordereau/${this.bordereauData._id}`
        );

        if (response.data.success) {
          // Update the bordereauData with full details
          Object.assign(this.bordereauData, response.data.data);
        }
      } catch (error) {
        console.error("Error fetching full bordereau data:", error);
        this.$message.error(
          "Erreur lors du chargement des détails du bordereau"
        );
      } finally {
        this.loading = false;
      }
    },

    async updateChequeBankStatus(chequeId, newStatus) {
      try {
        // Find the cheque and set updating flag
        const chequeIndex = this.bordereauData.chequesDetails.findIndex(
          (cheque) => cheque._id === chequeId
        );

        if (chequeIndex !== -1) {
          this.$set(
            this.bordereauData.chequesDetails[chequeIndex],
            "updating",
            true
          );
        }

        const response = await apiClient.patch(
          `/compV3/cheque-traite/${chequeId}/bank-status`,
          { bankStatus: newStatus }
        );

        if (response.data.success) {
          this.$message.success(
            `Statut du ${this.typeLabelSingular} mis à jour avec succès et ${this.typeLabelSingular} transféré au caisse bancaire avec succès`
          );

          // Update the local data
          if (chequeIndex !== -1) {
            this.$set(
              this.bordereauData.chequesDetails[chequeIndex],
              "bankStatus",
              newStatus
            );
            this.$set(
              this.bordereauData.chequesDetails[chequeIndex],
              "updating",
              false
            );
          }

          // Emit update event to parent component but don't close modal
          this.$emit("updated", this.bordereauData);
        } else {
          this.$message.error(
            response.data.message || "Erreur lors de la mise à jour du statut"
          );

          // Reset updating flag on error
          if (chequeIndex !== -1) {
            this.$set(
              this.bordereauData.chequesDetails[chequeIndex],
              "updating",
              false
            );
          }
        }
      } catch (error) {
        if (
          error.response &&
          error.response.data &&
          error.response.data.errorCode
        ) {
          this.$message.error(
            this.$t(`errorCode.${error.response.data.errorCode}`)
          );
        } else {
          this.$message.error(this.$t("error.erreur"));
        }

        // Reset updating flag on error
        const chequeIndex = this.bordereauData.chequesDetails.findIndex(
          (cheque) => cheque._id === chequeId
        );
        if (chequeIndex !== -1) {
          this.$set(
            this.bordereauData.chequesDetails[chequeIndex],
            "updating",
            false
          );
        }
      }
    },

    handleCancel() {
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.ant-form-item {
  margin-bottom: 16px;
}

.ant-modal-body {
  padding: 24px;
}

.clickable {
  cursor: pointer;
}

.clickable:hover {
  text-decoration: underline;
}
</style>
